import Phaser from "phaser";
import { BaseLevelScene, objProp } from "./BaseLevelScene";
import { levelById } from "../levels";
import { Prop } from "../tiles";
import { Tex } from "../ui/textures";
import { Palette } from "../ui/palette";
import { A as LUCAS_ANIM, LUCAS_ATLAS, LUCAS_SCALE, registerLucasAnims, useLucasSprite, updateLucasAnim } from "../ui/lucasAnim";
import bikeAlign from "../data/lucas-bike-align.json";

const RUN_SPEED = 340; // roof level is larger; ~2x the old rig-level speed
const JUMP_SPEED = 420;
const CLIMB_SPEED = 180; // vertical speed while on a ladder tile
const TIME_LIMIT = 180; // seconds - the whole budget (three minutes)

// "Boot the Bionic sign": Lucas has to power up the BIONIC sign on the roof rig.
// Flow (all positions come from Tiled objects so they can be nudged against the art):
//   1. Arrival: the upper door and the garage are LOCKED, the three battery slots are
//      EMPTY, and the bike has no cable (overlays drawn over the base art, which shows
//      the "done" state: doors open, batteries full, sign off).
//   2. Find the GARAGE key -> open the garage -> grab the CABLE inside.
//   3. Carry the cable to the bike power area (cable_anchor) -> it attaches (bike-cable
//      art appears). Only now can Lucas mount the bike.
//   4. Ride the bike: the BIONIC sign fills. Each BATTERY he carries into an empty slot
//      makes it fill faster (rate scales with filled slots).
//   5. Find the UPPER-door key -> open the upper door (he can walk BEHIND it via a mask)
//      -> exit once the sign is full.
const SIGN_RATE = 0.025; // sign fill/sec at base (no batteries); +100% per filled slot
const SIGN_FILL_LTR = false; // fill the sign left-to-right (false = bottom-up)

// On-screen sizes (px) for the rig items; source art is large, scaled down by code.
const BATTERY_PX = 72; // carryable battery pickup
const BATTERY_ANIM = "battery-charge"; // 20-frame crackle loop (battery.png spritesheet)
const BATTERY_INSTALL_START_ANIM = "battery-install-start"; // one-shot power-up when seated
const BATTERY_INSTALL_LOOP_ANIM = "battery-install-loop"; // steady loop after the power-up
// The installed-battery casing fills ~218px of its 256px frame; scale so that casing
// matches the empty-slot overlay it replaces.
const INSTALL_FRAME_CASING_H = 218;
const KEY_PX = 92; // key pickup
const CABLE_PX = 84; // coiled cable pickup
const BG_DIM = 0.3; // opacity of the dark mask over the roof background (0 = none, 1 = black)
const BIKE_CABLE_H = 525; // the wired bike-cable height when attached (matches the 128px terminal scale)
const BIKE_CABLE_DX = 32; // nudge the wired cable art right of the cable_anchor
const BIKE_CABLE_DY = 50; // nudge the wired cable art down from the cable_anchor
const TERMINAL_H = 118; // on-screen height of the "BIKE POWER" terminal (aspect preserved)
const CARRY_PX = 30; // the little icon floating over Lucas showing what he carries

// The process station is an exercise bike Lucas mounts to run the rig. Three art
// pieces sharing a 256px cell (so they register): a STANDALONE idle bike, a one-shot
// MOUNT clip (Lucas walks in and gets on), and a looping PEDAL clip. The mount's last
// frame lines up with the pedal's first, so mount flows straight into pedal. During
// the mount's walk-in the clip's bike is clipped off the cell's right edge, so the
// standalone bike is shown to complete it until the clip draws the full bike.
const BIKE_TEX = "lucas-bike-standalone";
const PEDAL_TEX = "tex-lucas-pedal";
const MOUNT_TEX = "tex-lucas-mount";
const PEDAL_ANIM = "lucas-pedal";
const MOUNT_ANIM = "lucas-mount";
const PEDAL_FRAMES = 13;
const MOUNT_FRAMES = 12; // "quick" mount (every other frame of the original 23)
const RIDE_FPS = 14;

// The mount/pedal art is authored in a big 256px cell; scale it down so the seated
// Lucas matches his walking sprite. Origins seat the content (not the empty cell
// padding) on the ledge floor: content bottom (wheels) at y~231, seated bike centered
// near x140 in the cell. The standalone bike is scaled/placed to read as the same bike.
// Sizing + per-frame alignment are hand-authored in public/bike-align.html and stored
// in lucas-bike-align.json: the clip's bike PANS within its own frame as Lucas gets on,
// so `mount[frame]` is the X offset that slides each frame back onto the fixed standalone
// bike; `oy` is a shared vertical offset. Both bikes use an origin-(0.5,1) floor anchor.
// The rig was hand-authored against walking Lucas at scale 1.0; multiply the whole
// rig (rider scale, standalone width, vertical offset, and the per-frame X offsets in
// positionRider) by LUCAS_SCALE so it grows with him uniformly. Uniform scaling about
// the fixed floor anchor is a similarity transform, so the mount stays aligned.
const RIDER_SCALE = bikeAlign.riderScale * LUCAS_SCALE; // seated Lucas matches walking Lucas
const STANDALONE_BIKE_W = bikeAlign.standaloneW * LUCAS_SCALE; // standalone bike on-screen width (px)
const BIKE_OY = bikeAlign.oy * LUCAS_SCALE;
const BIKE_DROP = 3; // lower both bikes together so Lucas's walk-in feet meet the floor
const STANDALONE_EXTRA_DROP = 3; // standalone bike only, nudged down to sit under the rider
const RIDER_LIFT = 6; // seated rider only: bump up so he isn't a hair low vs the standalone bike/floor

interface BatterySlot {
  x: number;
  y: number;
  filled: boolean;
  empty?: Phaser.GameObjects.Image; // "empty battery" overlay; destroyed on fill to reveal the full battery in the base art
  installed?: Phaser.GameObjects.Sprite; // animated battery shown once a battery is seated (power-up then loop)
}

export class Level2LucasScene extends BaseLevelScene {
  private processPad?: Phaser.Physics.Arcade.Image;
  private bikeIdle?: Phaser.GameObjects.Image; // standalone bike (idle + walk-in fill)
  private rider?: Phaser.GameObjects.Sprite; // Lucas pedal clip (256px cell)
  private riderState: "off" | "mounting" | "riding" = "off";
  private bikeFA?: { x: number; y: number }; // fixed screen point the flywheel pins to
  private climbing = false; // true while Lucas is on a ladder tile moving vertically
  private timeLeft = TIME_LIMIT;

  // --- boot-the-sign mechanic state ---
  private hasGarageKey = false;
  private hasUpperKey = false;
  private garageOpen = false;
  private garageLockedMsgAt = 0; // throttles the "garage locked" hint while overlapping
  private upperOpen = false;
  private cableAttached = false; // cable wired from power to bike; only now can he mount
  private carrying?: "battery" | "cable"; // what Lucas currently holds (one at a time)
  private signFill = 0; // 0..1 BIONIC sign charge
  private slots: BatterySlot[] = []; // battery slots; filled ones speed the sign
  private signLight?: Phaser.GameObjects.Image;
  private cablePickup?: Phaser.Physics.Arcade.Image; // the cable inside the garage
  private carryIcon?: Phaser.GameObjects.Sprite; // little sprite showing what he's carrying
  private maskPos?: { x: number; y: number }; // optional precise spot for the open-door foreground mask
  private signW = 1; // cached sign texture width (for the left-to-right crop)
  private signH = 1;
  private bikeHintShown = false; // "need the cable" nudge, shown once per approach
  private signBooted = false; // sign hit 100% (fire the "head to the exit" beat once)

  constructor() {
    super("Level2Lucas");
  }

  create() {
    this.processPad = undefined;
    this.bikeIdle = undefined;
    this.rider = undefined;
    this.riderState = "off";
    this.climbing = false;
    this.bikeFA = undefined;
    this.timeLeft = TIME_LIMIT;
    this.hasGarageKey = false;
    this.hasUpperKey = false;
    this.garageOpen = false;
    this.upperOpen = false;
    this.cableAttached = false;
    this.carrying = undefined;
    this.signFill = 0;
    this.slots = [];
    this.signLight = undefined;
    this.cablePickup = undefined;
    this.carryIcon = undefined;
    this.maskPos = undefined;
    this.bikeHintShown = false;
    this.signBooted = false;

    this.buildWorld(levelById("lucas"));
    this.world.setVisible(false); // invisible floor: Lucas runs on the painted roof

    // Dim the busy roof background so the interactive props (batteries, keys, cable,
    // doors) read clearly. Sits above the image layer (-10) but below every prop (0+),
    // pinned to the camera so it covers the viewport as it scrolls.
    this.add
      .rectangle(this.scale.width / 2, this.scale.height / 2, this.scale.width, this.scale.height, 0x000000, BG_DIM)
      .setScrollFactor(0)
      .setDepth(-5);

    const spawn = this.findSpawn();
    this.createPlayer(spawn.x, spawn.y);
    this.buildLiftedFloors(); // per-tile quarter-tile-raised floor variant (needs this.player)
    if (this.textures.exists(LUCAS_ATLAS)) {
      registerLucasAnims(this);
      useLucasSprite(this.player);
    }

    this.forEachObject((obj, cls) => {
      switch (cls) {
        case "sheepy":
          this.spawnSheepy(obj);
          break;
        case "process_pad":
          this.makeProcessPad(obj);
          break;
        case "battery":
          this.makeBattery(obj);
          break;
        case "key":
          this.makeKey(obj);
          break;
        case "bionic_sign":
          this.makeBionicSign(obj);
          break;
        case "garage_door":
          this.makeGarageDoor(obj);
          break;
        case "upper_door":
          this.makeUpperDoor(obj);
          break;
        case "door_mask":
          this.makeDoorMask(obj);
          break;
        case "battery_slot":
          this.makeBatterySlot(obj);
          break;
        case "cable_pickup":
          this.makeCablePickup(obj);
          break;
        case "cable_anchor":
          this.makeCableAnchor(obj);
          break;
        case "bike_terminal":
        case "bike_power":
          this.makeBikeTerminal(obj);
          break;
        case "exit":
          this.makeExit(obj);
          break;
      }
    });

    // The roof map is much larger than one screen, so follow Lucas.
    this.cameras.main.setBounds(0, 0, this.map.widthInPixels, this.map.heightInPixels);
    this.cameras.main.startFollow(this.player, true, 0.1, 0.1);

    this.buildHud();
    this.buildInput();
    this.tickTimer();
    this.refreshInfo();
    this.flashText("Power up Bionic by connecting the bike to power.", Palette.lucas);
  }

  // ---- the rig -------------------------------------------------------------

  private makeProcessPad(obj: Phaser.Types.Tilemaps.TiledObject) {
    // The pad is the (hidden) mount zone; the bike sits on top of it.
    this.processPad = this.physics.add.staticImage(obj.x!, obj.y! + 12, Tex.processPad).setVisible(false);
    const floorY = obj.y! + 16;
    // Floor anchor (bottom-center) shared by both bikes, matching public/bike-align.html
    // (dropped a few px so the walk-in feet meet the floor).
    this.bikeFA = { x: obj.x!, y: floorY + BIKE_DROP };

    // Standalone idle bike: shown when nobody's on it, and during the mount clip's
    // walk-in (the clip's own bike is clipped off-frame then). Origin (0.5,1) on the floor.
    if (this.textures.exists(BIKE_TEX)) {
      const b = this.add.image(this.bikeFA.x, this.bikeFA.y + STANDALONE_EXTRA_DROP, BIKE_TEX).setOrigin(0.5, 1).setDepth(4);
      b.setScale(STANDALONE_BIKE_W / b.width);
      this.bikeIdle = b;
    }

    // Rider clips (mount then pedal): a 256px-cell sprite scaled to match walking Lucas.
    // Origin (0.5,1) on the floor; per-frame X (positionRider) slides it onto the standalone.
    if (this.textures.exists(PEDAL_TEX)) {
      if (!this.anims.exists(PEDAL_ANIM))
        this.anims.create({
          key: PEDAL_ANIM,
          frames: this.anims.generateFrameNumbers(PEDAL_TEX, { start: 0, end: PEDAL_FRAMES - 1 }),
          frameRate: RIDE_FPS,
          repeat: -1,
        });
      if (this.textures.exists(MOUNT_TEX) && !this.anims.exists(MOUNT_ANIM))
        this.anims.create({
          key: MOUNT_ANIM,
          frames: this.anims.generateFrameNumbers(MOUNT_TEX, { start: 0, end: MOUNT_FRAMES - 1 }),
          frameRate: RIDE_FPS,
          repeat: 0,
        });
      this.rider = this.add
        .sprite(this.bikeFA.x, this.bikeFA.y, PEDAL_TEX, 0)
        .setOrigin(0.5, 1)
        .setScale(RIDER_SCALE)
        .setDepth(5)
        .setVisible(false);
    }

  }

  /** A carryable battery pickup. Touch to carry (one item at a time); drop it in an
   *  empty slot to speed the sign. */
  private makeBattery(obj: Phaser.Types.Tilemaps.TiledObject) {
    this.ensureBatteryAnim();
    const b = this.physics.add.staticSprite(obj.x!, obj.y!, Tex.battery).play(BATTERY_ANIM);
    b.setScale(BATTERY_PX / Math.max(b.width, b.height)).refreshBody();
    this.bob(b);
    this.physics.add.overlap(this.player, b, () => {
      if (!b.active || this.carrying) return;
      b.destroy();
      this.carrying = "battery";
      this.setCarryIcon(Tex.battery, BATTERY_ANIM);
      this.flashText("Picked up a battery - drop it in an empty slot", Palette.lucas);
    });
  }

  /** Create the battery crackle loop once (safe across scene restarts). */
  private ensureBatteryAnim() {
    if (this.anims.exists(BATTERY_ANIM)) return;
    this.anims.create({
      key: BATTERY_ANIM,
      frames: this.anims.generateFrameNumbers(Tex.battery, { start: 0, end: 19 }),
      frameRate: 12,
      repeat: -1,
    });
  }

  /** Give a pickup a gentle floating bob, with randomized amplitude, speed, and starting
   *  phase so batteries and keys don't all bounce in lockstep. */
  private bob(target: Phaser.GameObjects.Components.Transform & { y: number }) {
    const amp = Phaser.Math.Between(7, 12);
    this.tweens.add({
      targets: target,
      y: target.y - amp,
      duration: Phaser.Math.Between(680, 1000),
      delay: Phaser.Math.Between(0, 500),
      yoyo: true,
      repeat: -1,
      ease: "Sine.inOut",
    });
  }

  /** Create the seated-battery power-up (once) + loop anims (safe across restarts). */
  private ensureInstalledAnims() {
    if (!this.anims.exists(BATTERY_INSTALL_START_ANIM)) {
      this.anims.create({
        key: BATTERY_INSTALL_START_ANIM,
        frames: this.anims.generateFrameNumbers(Tex.batteryInstallStart, { start: 0, end: 16 }),
        frameRate: 20,
        repeat: 0,
      });
    }
    if (!this.anims.exists(BATTERY_INSTALL_LOOP_ANIM)) {
      this.anims.create({
        key: BATTERY_INSTALL_LOOP_ANIM,
        frames: this.anims.generateFrameNumbers(Tex.batteryInstallLoop, { start: 0, end: 7 }),
        frameRate: 12,
        repeat: -1,
      });
    }
  }

  /** Two keys: kind "garage" (opens the garage for the cable) and "upper" (opens the
   *  upper exit door). Default garage. */
  private makeKey(obj: Phaser.Types.Tilemaps.TiledObject) {
    const kind = objProp<string>(obj, Prop.KIND, "garage");
    const key = this.physics.add.staticImage(obj.x!, obj.y!, Tex.key);
    key.setScale(KEY_PX / Math.max(key.width, key.height)).refreshBody();
    this.bob(key);
    this.physics.add.overlap(this.player, key, () => {
      if (!key.active) return;
      key.destroy();
      if (kind === "upper") {
        this.hasUpperKey = true;
        this.flashText("Got the upper-door key", Palette.lucas);
      } else {
        this.hasGarageKey = true;
        this.flashText("Got the garage key", Palette.lucas);
      }
    });
  }

  /** The BIONIC sign light overlay (over the dark sign in the base art). Cropped to the
   *  current charge; grows as Lucas rides. */
  private makeBionicSign(obj: Phaser.Types.Tilemaps.TiledObject) {
    const s = this.add.image(obj.x!, obj.y!, Tex.bionicSign).setDepth(0);
    this.signW = s.width;
    this.signH = s.height;
    this.signLight = s;
    this.updateSign();
  }

  /** Garage shutter overlay: locked until the garage key is held. Opening it reveals
   *  the cable pickup inside. Non-blocking: Lucas can walk past it even while locked;
   *  he just can't open it (reveal the cable) without the key. */
  private makeGarageDoor(obj: Phaser.Types.Tilemaps.TiledObject) {
    const img = this.add.image(obj.x!, obj.y!, Tex.garageDoor).setDepth(2);
    const zone = this.add.zone(obj.x!, obj.y!, img.width, img.height);
    this.physics.add.existing(zone, true);
    // Overlap (not collider) so it never blocks passage. Touch it with the key to open;
    // without the key it just shows a throttled locked hint.
    const overlap = this.physics.add.overlap(this.player, zone, () => {
      if (this.garageOpen) return;
      if (!this.hasGarageKey) {
        if (this.time.now - this.garageLockedMsgAt > 2000) {
          this.garageLockedMsgAt = this.time.now;
          this.flashText("The garage is locked - find the key", Palette.textDim);
        }
        return;
      }
      this.garageOpen = true;
      img.destroy();
      overlap.destroy();
      zone.destroy();
      this.cablePickup?.setVisible(true).setActive(true);
      this.flashText("Garage open - grab the bike cable inside", Palette.lucas);
    });
  }

  /** Upper door overlay: locked until the upper key is held. Opening it swaps in the
   *  foreground mask so Lucas walks BEHIND the open door, and clears the block. */
  private makeUpperDoor(obj: Phaser.Types.Tilemaps.TiledObject) {
    const img = this.add.image(obj.x!, obj.y!, Tex.closedDoor).setDepth(2);
    const zone = this.add.zone(obj.x!, obj.y!, img.width, img.height);
    this.physics.add.existing(zone, true);
    // Open from the collider's OWN callback: it fires the instant Lucas touches the
    // door. A separate overlap would never fire, since the collider separates the
    // bodies before the overlap check runs that frame.
    const blocker = this.physics.add.collider(this.player, zone, () => {
      if (this.upperOpen) return;
      if (!this.hasUpperKey) {
        this.flashText("The upper door is locked - find its key", Palette.textDim);
        return;
      }
      this.upperOpen = true;
      img.destroy(); // the closed door is gone
      blocker.destroy(); // and it no longer blocks
      zone.destroy();
      // The open-door mask is a pure foreground overlay: depth above the player (5)
      // so Lucas passes BEHIND it, and it has no physics body so he can walk through it.
      const m = this.maskPos ?? { x: obj.x!, y: obj.y! };
      this.add.image(m.x, m.y, Tex.openDoorMask).setDepth(20);
      this.flashText("Upper door open", Palette.lucas);
    });
  }

  /** Optional precise anchor for the open-door foreground mask (else the upper_door spot). */
  private makeDoorMask(obj: Phaser.Types.Tilemaps.TiledObject) {
    this.maskPos = { x: obj.x!, y: obj.y! };
  }

  /** A battery slot. Walking onto it while carrying a battery loads it (seats a battery
   *  and speeds the sign). */
  private makeBatterySlot(obj: Phaser.Types.Tilemaps.TiledObject) {
    // The base art paints a FULL battery in each slot; an "empty battery" overlay sits
    // over it so it reads as empty. Loading a battery destroys the overlay, revealing
    // the full battery underneath. Overlay depth is below the player (5) so Lucas walks
    // in front of the panel.
    const empty = this.add.image(obj.x!, obj.y!, Tex.batteryEmpty).setDepth(1);
    const slot: BatterySlot = { x: obj.x!, y: obj.y!, filled: false, empty };
    this.slots.push(slot);
    const zone = this.add.zone(obj.x!, obj.y!, 40, 48);
    this.physics.add.existing(zone, true);
    this.physics.add.overlap(this.player, zone, () => {
      if (slot.filled || this.carrying !== "battery") return;
      slot.filled = true;
      this.carrying = undefined;
      this.clearCarryIcon();
      // Seat the battery: play the one-shot power-up, then chain to the steady loop,
      // sized so the casing matches the empty overlay it covers.
      this.ensureInstalledAnims();
      const targetH = 173 + 4; // seated battery casing height (empty overlay is 173), +4px
      const seated = this.add.sprite(slot.x, slot.y, Tex.batteryInstallStart).setDepth(1);
      seated.setScale(targetH / INSTALL_FRAME_CASING_H);
      seated.play(BATTERY_INSTALL_START_ANIM).chain(BATTERY_INSTALL_LOOP_ANIM);
      slot.installed = seated;
      slot.empty?.destroy(); // remove the empty overlay; the animated battery covers the base art
      slot.empty = undefined;
      const n = this.slots.filter((s) => s.filled).length;
      this.flashText(`Battery loaded (${n}/${this.slots.length}) - the sign charges faster`, Palette.lucas);
      this.refreshInfo();
    });
  }

  /** The cable, hidden inside the garage until it's opened. Carry it to the bike power area. */
  private makeCablePickup(obj: Phaser.Types.Tilemaps.TiledObject) {
    const c = this.physics.add.staticImage(obj.x!, obj.y!, Tex.cable).setDepth(1);
    c.setScale(CABLE_PX / Math.max(c.width, c.height)).refreshBody();
    c.setVisible(this.garageOpen).setActive(this.garageOpen);
    this.cablePickup = c;
    this.physics.add.overlap(this.player, c, () => {
      if (!c.active || !this.garageOpen || this.carrying) return;
      c.destroy();
      this.cablePickup = undefined;
      this.carrying = "cable";
      this.setCarryIcon(Tex.cable);
      this.flashText("Grabbed the cable - plug it into the power plug", Palette.lucas);
    });
  }

  /** The bike power area: bringing the cable here wires the bike (bike-cable art appears)
   *  and unlocks mounting. */
  /** The "BIKE POWER" terminal art. Purely decorative: place it in Tiled (object class
   *  `bike_terminal` or `bike_power`) wherever the cable should plug in. Scaled to
   *  TERMINAL_H, aspect preserved; sits behind the wired cable. */
  private makeBikeTerminal(obj: Phaser.Types.Tilemaps.TiledObject) {
    const img = this.add.image(obj.x!, obj.y!, Tex.bikeTerminal).setDepth(2);
    img.setScale(TERMINAL_H / img.height);
  }

  private makeCableAnchor(obj: Phaser.Types.Tilemaps.TiledObject) {
    const zone = this.add.zone(obj.x!, obj.y!, 56, 80);
    this.physics.add.existing(zone, true);
    this.physics.add.overlap(this.player, zone, () => {
      if (this.cableAttached || this.carrying !== "cable") return;
      this.cableAttached = true;
      this.carrying = undefined;
      this.clearCarryIcon();
      const img = this.add.image(obj.x! + BIKE_CABLE_DX, obj.y! + BIKE_CABLE_DY, Tex.bikeCable).setOrigin(0.5, 1).setDepth(3);
      img.setScale(BIKE_CABLE_H / img.height);
      this.flashText("Cable wired - hop on the bike to boot the sign", Palette.lucas);
    });
  }

  private makeExit(obj: Phaser.Types.Tilemaps.TiledObject) {
    const zone = this.add.zone(obj.x!, obj.y!, obj.width!, obj.height!).setOrigin(0, 0);
    this.physics.add.existing(zone, true);
    this.physics.add.overlap(this.player, zone, () => {
      if (this.signFill < 1) {
        this.flashText("Boot the BIONIC sign first (ride the bike)", Palette.textDim);
        return;
      }
      this.reachExit();
    });
  }

  // ---- carry icon + sign helpers ------------------------------------------

  private setCarryIcon(tex: string, anim?: string) {
    this.clearCarryIcon();
    const i = this.add.sprite(this.player.x, this.player.y, tex).setDepth(7);
    if (anim) i.play(anim);
    i.setScale(CARRY_PX / Math.max(i.width, i.height));
    this.carryIcon = i;
  }

  private clearCarryIcon() {
    this.carryIcon?.destroy();
    this.carryIcon = undefined;
  }

  /** Crop the sign-light overlay to the current charge (left-to-right or bottom-up). */
  private updateSign() {
    if (!this.signLight) return;
    const f = Phaser.Math.Clamp(this.signFill, 0, 1);
    // Snap crop edges to whole pixels: fractional crop coords round differently each
    // frame and make the fill edge jitter (worst bottom-up, where the Y moves every frame).
    if (SIGN_FILL_LTR) {
      this.signLight.setCrop(0, 0, Math.round(this.signW * f), this.signH);
    } else {
      const h = Math.round(this.signH * f);
      this.signLight.setCrop(0, this.signH - h, this.signW, h);
    }
  }

  // ---- HUD / timer / completion -------------------------------------------

  private tickTimer() {
    this.time.addEvent({
      delay: 1000,
      repeat: TIME_LIMIT - 1,
      callback: () => {
        if (this.finished) return;
        this.timeLeft--;
        this.refreshInfo();
        if (this.timeLeft <= 0) this.failLevel("Time ran out.");
      },
    });
  }

  private refreshInfo() {
    const pct = Math.floor(this.signFill * 100);
    const filled = this.slots.filter((s) => s.filled).length;
    const charging = this.riderState === "riding" ? " ▸" : "";
    // A two-row table: label left, value right.
    this.setHudInfo(
      this.hudTable([
        ["Bionic", `${pct}%${charging}`],
        ["batteries", `${filled} of ${this.slots.length}`],
      ]),
    );
    // Big yellow countdown in the corner, mm:ss, red under 20s.
    const m = Math.floor(this.timeLeft / 60);
    const s = this.timeLeft % 60;
    this.setHudTimer(`⏱ ${m}:${s.toString().padStart(2, "0")}`, this.timeLeft <= 20);
  }

  private reachExit() {
    const stars = this.timeLeft >= 40 ? 3 : this.timeLeft >= 15 ? 2 : 1;
    this.applySpeedBonus("lucas", TIME_LIMIT - this.timeLeft);
    this.completeLevel(stars, [
      'Way to go! Code & Co is now powerd by Bionic.',
      `${this.slots.filter((s) => s.filled).length}/${this.slots.length} batteries loaded · ${this.timeLeft}s to spare.`,
    ]);
  }

  override update(time: number, delta: number) {
    if (this.finished) return;

    const body = this.player.body as Phaser.Physics.Arcade.Body;
    // Read the jump edge ONCE (JustDown consumes it) so it isn't swallowed by the
    // wantsMove check before the jump handler below sees it.
    const jumpPressed = this.jumpJustDown();
    const wantsMove = this.leftDown() || this.rightDown() || jumpPressed;

    // Mounted = standing on the bike with no movement input AND the cable is wired.
    // Any direction hops off. Without the cable he just stands there (with a nudge).
    const onBike =
      !!this.processPad &&
      Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), this.processPad.getBounds());
    if (onBike && !this.cableAttached) {
      if (!this.bikeHintShown) {
        this.flashText("The bike has no cable - find it in the garage", Palette.textDim);
        this.bikeHintShown = true;
      }
    } else if (!onBike) {
      this.bikeHintShown = false;
    }
    const mounted = onBike && !wantsMove && this.cableAttached;

    if (mounted) {
      // Lucas is on the bike: hide his walking sprite; the rider clip takes over.
      this.player.setVelocityX(0);
      this.player.setVisible(false);
    } else {
      this.player.setVisible(true);
      const upHeld = this.upDown();
      const downHeld = this.keys.cursors.down?.isDown || this.keys.down.isDown;

      // Ladder climbing. Enter when up/down is pressed while on a ladder: `onLadder()`
      // samples down his (tall) body, and pressing down also grabs a ladder just below
      // his feet so he can step off the floor above. Once climbing he hangs with gravity
      // off (holding no direction = stay put); jumping or clearing the ladder drops him.
      const onLadder = this.onLadder();
      const stepDown = downHeld && this.ladderBelow();
      if (!this.climbing && ((upHeld && onLadder) || stepDown)) this.climbing = true;
      if (this.climbing && (jumpPressed || (!onLadder && !stepDown))) this.climbing = false;

      if (this.climbing) {
        body.setAllowGravity(false);
        this.player.setVelocityX(this.leftDown() ? -RUN_SPEED : this.rightDown() ? RUN_SPEED : 0);
        this.player.setVelocityY(upHeld ? -CLIMB_SPEED : downHeld ? CLIMB_SPEED : 0);
        this.player.setFlipX(false); // climb clip is a back view; keep it upright
        if (this.player.anims.currentAnim?.key !== LUCAS_ANIM.climb) this.player.play(LUCAS_ANIM.climb, true);
        if (upHeld || downHeld) this.player.anims.resume();
        else this.player.anims.pause(); // freeze the cycle while hanging still
      } else {
        if (!body.allowGravity) body.setAllowGravity(true);
        if (this.leftDown()) this.player.setVelocityX(-RUN_SPEED);
        else if (this.rightDown()) this.player.setVelocityX(RUN_SPEED);
        else this.player.setVelocityX(0);
        if (jumpPressed && body.blocked.down) this.player.setVelocityY(-JUMP_SPEED);
        if (this.textures.exists(LUCAS_ATLAS)) updateLucasAnim(this.player);
      }
    }

    this.updateRider(mounted);

    // Riding charges the BIONIC sign. Rate rises with each filled battery slot (+100%
    // of the base rate each), so more batteries = faster boot.
    if (this.riderState === "riding" && this.signFill < 1) {
      const filled = this.slots.filter((s) => s.filled).length;
      this.signFill = Math.min(1, this.signFill + SIGN_RATE * (1 + filled) * (delta / 1000));
      this.updateSign();
      this.refreshInfo();
      if (this.signFill >= 1 && !this.signBooted) {
        this.signBooted = true;
        this.flashText("Bionic fully powered! Head for the exit.", Palette.lucas);
      }
    }

    // The carried item floats over Lucas's head.
    if (this.carryIcon) this.carryIcon.setPosition(this.player.x, this.player.y - this.player.displayHeight * 0.55);
  }

  /** Raised-floor variant: paint the PLATFORM tile (gid 4, the 4th office tile) where a
   *  floor's painted surface sits a quarter tile above the grid. Its normal full-tile
   *  collision is removed and replaced with a body whose top is 8px higher, so Lucas's
   *  feet plant on the painted floor. The plain GROUND tile (gid 1) stays at grid level,
   *  so you mix the two per floor.
   *
   *  These lifted bodies are ONE-WAY (jump-through): Lucas lands on them from above but
   *  passes up through them (so he can climb a ladder up through a platform). This mirrors
   *  the tile-level `oneway` logic; the platform tile carries `oneway` in the tileset, but
   *  its tile collision is dropped here, so we re-apply the same rule to the lifted body. */
  private buildLiftedFloors() {
    const LIFT = 8;
    const RAISED_GID = 4; // office tile id 3 (platform)
    this.world.setCollision(RAISED_GID, false); // drop its full-tile collision; use a lifted body instead
    const group = this.physics.add.staticGroup();
    this.world.forEachTile((t) => {
      if (t.index !== RAISED_GID) return;
      const zone = this.add.zone(t.pixelX + t.width / 2, t.pixelY - LIFT + (t.height + LIFT) / 2, t.width, t.height + LIFT);
      this.physics.add.existing(zone, true);
      group.add(zone);
    });
    this.physics.add.collider(this.player, group, undefined, (_player, zoneObj) => {
      if (this.downDown()) return false; // hold down to drop through a one-way platform
      const body = this.player.body as Phaser.Physics.Arcade.Body;
      const zbody = (zoneObj as Phaser.GameObjects.Zone).body as Phaser.Physics.Arcade.StaticBody;
      // Land only when moving down and the player's feet were above the body's top last frame.
      return body.velocity.y >= 0 && body.prev.y + body.height <= zbody.top + 4;
    });
  }

  /** Let mid-floor / jump-through tiles pass through while Lucas is on a ladder. */
  protected override isClimbing(): boolean {
    return this.climbing;
  }

  /** True when a ladder tile overlaps Lucas's body. His sprite is ~6 tiles tall, so a
   *  single centre sample misses; this walks several points down the body. Ladder tiles
   *  (id 5, `ladder` property) are non-solid, painted in the world layer over the roof
   *  art's ladders. */
  private onLadder(): boolean {
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    const x = body.center.x;
    const step = this.map.tileHeight * 0.5;
    for (let y = body.top + 4; y <= body.bottom - 2; y += step) {
      const t = this.world?.getTileAtWorldXY(x, y);
      if (t && t.properties?.[Prop.LADDER]) return true;
    }
    return false;
  }

  /** True when a ladder tile sits just below his feet, so holding down steps him off the
   *  floor above onto the ladder. */
  private ladderBelow(): boolean {
    const body = this.player.body as Phaser.Physics.Arcade.Body;
    const t = this.world?.getTileAtWorldXY(body.center.x, body.bottom + this.map.tileHeight * 0.5);
    return !!t && !!t.properties?.[Prop.LADDER];
  }

  /** The rider clip's current frame index (mount 0..22 or pedal 0..12). */
  private riderFrameIndex(): number {
    return this.rider ? +(this.rider.frame.name || 0) : 0;
  }

  /** Slide the current frame onto the fixed standalone bike, using the hand-authored
   *  per-frame X offset (the clip's bike pans within its own frame as Lucas gets on). */
  private positionRider() {
    if (!this.rider || !this.bikeFA) return;
    const table = this.riderState === "mounting" ? bikeAlign.mount : bikeAlign.pedal;
    const i = Math.min(this.riderFrameIndex(), table.length - 1);
    const ox = (table[i] ?? 0) * LUCAS_SCALE; // scale the per-frame slide with the rig
    this.rider.setPosition(this.bikeFA.x + ox, this.bikeFA.y + BIKE_OY - RIDER_LIFT);
  }

  /**
   * Drive the mount cinematic: off -> mounting (play the getting-on clip once) ->
   * riding (loop pedal). The standalone bike fills the clip's clipped-off bike during
   * the walk-in, then hides once the clip draws the full bike (MOUNT_SHOW_BIKE_UNTIL).
   */
  private updateRider(mounted: boolean) {
    if (!this.rider) return;
    if (mounted) {
      if (this.riderState === "off") {
        // Skip the mount clip (it read badly): drop straight onto the pedal loop.
        this.rider.setVisible(true);
        this.riderState = "riding";
        this.rider.play(PEDAL_ANIM);
      }
      this.positionRider(); // pin the flywheel every frame (the clip's bike pans)
      if (this.bikeIdle) this.bikeIdle.setVisible(false); // clip draws the bike now
    } else if (this.riderState !== "off") {
      this.riderState = "off";
      this.rider.stop();
      this.rider.setVisible(false);
      if (this.bikeIdle) this.bikeIdle.setVisible(true);
    }
  }
}
